import validator from 'validator';

import { type DataType, dataType } from '../types';

const usPhoneCountryCode = '1';

const dataTypeValidators: Record<DataType, (value: string) => boolean> = {
  email: validator.isEmail,
  // validator 13.15+ enforces RFC9562 by default; 'loose' preserves prior UUID-like acceptance
  maid: (value: string) => validator.isUUID(value, 'loose'),
  phone: (value: string) => validator.isMobilePhone(value, 'en-US'),
};

export function normalizePhoneNumber(phoneNumber: string): string {
  const result = phoneNumber
    .trim()
    .replace(/["'\t ,]/g, '')
    .replace(/^\+/g, '')
    .replace(/[()-]/g, '')
    .replace(/(\([a-zA-Z]+\)|[a-zA-Z]+)/, '')
    .toLowerCase();

  const noCountryCode = result.length === 10;

  return noCountryCode ? usPhoneCountryCode + result : result;
}

export function normalizeValue(value: string, type: DataType): string {
  if (type === dataType.PHONE) {
    return normalizePhoneNumber(value);
  }

  return value.trim().toLowerCase();
}

export function isValueValid(value: string, type: DataType): boolean {
  return dataTypeValidators[type](value);
}

export async function hashValue(value: string, type: DataType): Promise<string> {
  if (type === dataType.MOBILE_AD_ID) {
    return value;
  }

  const msgUint8 = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

export function hashData(data: string[][], type: DataType): Promise<string[][]> {
  const promises = data
    .map(values => {
      const value = values[0];
      const alreadyHashed = validator.isHash(value, 'sha256');

      return {
        alreadyHashed,
        value: alreadyHashed ? value : normalizeValue(value, type),
      };
    })
    .filter(({ value, alreadyHashed }) => alreadyHashed || isValueValid(value, type))
    .map(({ value, alreadyHashed }) =>
      (alreadyHashed ? Promise.resolve(value) : hashValue(value, type)).then(value => [value])
    );

  return Promise.all(promises);
}
